import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "", as = "div" }) => {
  const reduce = useReducedMotion();
  const Comp = motion[as];
  return (
    <Comp
      className={className}
      initial={reduce ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Comp>
  );
};

export const SectionHeader = ({ eyebrow, title, intro, align = "left", className = "", id }) => (
  <Reveal className={`${align === "center" ? "mx-auto text-center" : ""} max-w-3xl ${className}`}>
    {eyebrow && <p className="eyebrow">{eyebrow}</p>}
    <h2 id={id} className="mt-4 text-3xl font-semibold leading-[1.1] sm:text-4xl lg:text-[2.75rem]">{title}</h2>
    {intro && <p className="mt-5 text-base leading-relaxed text-slate md:text-lg">{intro}</p>}
  </Reveal>
);

export const StagePill = ({ children = "Idea stage · Proposed model", className = "", testId = "stage-pill" }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-2 rounded-full border border-plum/15 bg-white/80 px-3.5 py-1.5 text-xs font-semibold text-plum backdrop-blur ${className}`}>
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-magenta opacity-60" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-magenta" />
    </span>
    {children}
  </span>
);

export const ProposedBadge = ({ label = "Proposed" }) => (
  <span className="inline-flex items-center rounded-full bg-plum-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-plum-700">
    {label}
  </span>
);

const btnBase = "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold transition-[background-color,color,border-color,transform] duration-200 active:scale-95";
const variants = {
  primary: "bg-teal text-white hover:bg-teal-700",
  secondary: "border border-plum/20 bg-white text-plum hover:border-plum/40 hover:bg-mint",
  light: "bg-white text-plum hover:bg-mint",
  ghostLight: "border border-white/30 text-white hover:bg-white/10",
};

export const CtaLink = ({ to, children, variant = "primary", testId, arrow = true, className = "" }) => (
  <Link to={to} data-testid={testId} className={`group ${btnBase} ${variants[variant]} ${className}`}>
    {children}
    {arrow && <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />}
  </Link>
);

export const IconTile = ({ icon: Icon, tone = "teal" }) => (
  <span className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone === "plum" ? "bg-plum-50 text-plum" : tone === "white" ? "bg-white/10 text-white" : "bg-teal-50 text-teal-700"}`}>
    <Icon className="h-[22px] w-[22px]" strokeWidth={1.7} />
  </span>
);
